import { toast } from 'react-toast';

export const NETWORK_ERROR_MESSAGE =
  'No internet connection. Please check your network and try again.';

const NETWORK_TOAST_COOLDOWN_MS = 4000;
let lastNetworkToastAt = 0;
let suppressOtherToastsUntil = 0;

// Preserve originals so we can still show the network toast while suppressing
// subsequent local toast.error(...) calls from page catch blocks.
const originalToastError = toast.error.bind(toast);
const originalToastSuccess = toast.success.bind(toast);
const originalToastInfo = toast.info?.bind(toast);

toast.error = (content, config) => {
  if (Date.now() < suppressOtherToastsUntil) {
    return;
  }
  return originalToastError(content, config);
};

toast.success = (content, config) => {
  if (Date.now() < suppressOtherToastsUntil) {
    return;
  }
  return originalToastSuccess(content, config);
};

if (originalToastInfo) {
  toast.info = (content, config) => {
    if (Date.now() < suppressOtherToastsUntil) {
      return;
    }
    return originalToastInfo(content, config);
  };
}

/**
 * Detect browser offline / Axios network / timeout failures.
 * These have no HTTP response from the server.
 */
export function isNetworkError(error) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  if (!error) return false;

  const code = error.code;
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return true;
  }

  if (error.message === 'Network Error') {
    return true;
  }

  // Request was made but no response received (typical offline / DNS / CORS block)
  if (!error.response && error.request) {
    return true;
  }

  return false;
}

/**
 * Resolve a user-facing API error message.
 * Network failures always use the standardized copy.
 */
export function getApiErrorMessage(
  error,
  fallback = 'Something went wrong. Please try again.'
) {
  if (isNetworkError(error)) {
    return NETWORK_ERROR_MESSAGE;
  }

  const data = error?.response?.data;
  const message =
    data?.message ||
    data?.detail?.message ||
    (typeof data?.detail === 'string' ? data.detail : null) ||
    error?.message;

  if (message && String(message).trim() && message !== 'Network Error') {
    return String(message);
  }

  return fallback;
}

/**
 * Show the standardized network toast at most once per cooldown window.
 * Clears any existing toasts and suppresses other toast calls briefly so
 * local catch blocks cannot show a second message for the same failure.
 */
export function notifyNetworkError() {
  const now = Date.now();
  if (now - lastNetworkToastAt < NETWORK_TOAST_COOLDOWN_MS) {
    // Still suppress local toasts that fire after the first network toast
    suppressOtherToastsUntil = Math.max(
      suppressOtherToastsUntil,
      now + NETWORK_TOAST_COOLDOWN_MS
    );
    return;
  }

  lastNetworkToastAt = now;
  suppressOtherToastsUntil = now + NETWORK_TOAST_COOLDOWN_MS;

  toast.hideAll();
  originalToastError(NETWORK_ERROR_MESSAGE);
}

/**
 * Convenience: if this is a network error, toast (debounced) and return true
 * so callers can skip their own toast/error UI.
 */
export function handleNetworkErrorToast(error) {
  if (!isNetworkError(error)) {
    return false;
  }
  notifyNetworkError();
  return true;
}
